import React, { ReactElement, useEffect, useState, useRef } from 'react'
import { Box, Button, Card, Skeleton, Typography } from '@mui/material'
import MigrateIcon from '@mui/icons-material/Transform'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchEventAccreditations,
  fetchCourseAccreditations,
  fetchV1MigrationPendingIds,
  migrateAccreditationsFromLiveClass,
  migrateCourseAccreditations,
  selectEventAccreditationsByEventID,
  selectEventAccreditationsStatusByEventID,
  selectCourseAccreditationsByCourseID,
  selectCourseAccreditationsStatusByCourseID,
  selectV1MigrationEntries,
  selectV1MigrationStatus,
} from 'src/v3/content-pipeline/accreditation.slice'
import {
  fetchEvents,
  selectEvents,
  selectEventsStatus,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import {
  fetchCourses,
  selectCourses,
  selectCoursesStatus,
} from 'src/v3/content-pipeline/performance.slice'
import { EventList } from '../performance/components/event-list'
import LifecycleChip from '../components/lifecycle/lifecycle-chip'
import AccreditationHealthDashboard from './components/accreditation-health-dashboard'
import GenerateFloridaApplicationButton from './components/generate-florida-application-button'
import { CAPCESection } from './components/capce'
import { VirginiaSection } from './components/virginia'
import { CEBrokerKentuckySection } from './components/cebroker-kentucky'
import { CEBrokerTennesseeSection } from './components/cebroker-tennessee'
import { CEBrokerFloridaSection } from './components/cebroker-florida'
import { NewJerseySection } from './components/new-jersey'
import { NewYorkSection } from './components/new-york'
import { ANCCSection } from './components/ancc'

type ContentDomain = 'live' | 'enduring'

type AccreditationDashboardProps = {
  domain: ContentDomain
}

export default ({ domain }: AccreditationDashboardProps): ReactElement => {
  let dispatch = useAppDispatch()
  let elementRef = useRef(null)

  let [activeItem, setActiveItem] = useState<string>()
  let [migrating, setMigrating] = useState(false)

  useEffect(() => {
    setActiveItem(undefined)
  }, [domain])

  let events = useAppSelector(selectEvents)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  let v1MigrationEntries = useAppSelector(selectV1MigrationEntries)
  let v1MigrationStatus = useAppSelector(selectV1MigrationStatus)

  // Get accreditation data for active item (domain-aware)
  let eventAccreditationData = useAppSelector(
    selectEventAccreditationsByEventID(
      domain === 'live' ? activeItem || '' : '',
    ),
  )
  let eventAccreditationStatus = useAppSelector(
    selectEventAccreditationsStatusByEventID(
      domain === 'live' ? activeItem || '' : '',
    ),
  )
  let courseAccreditationData = useAppSelector(
    selectCourseAccreditationsByCourseID(
      domain === 'enduring' ? activeItem || '' : '',
    ),
  )
  let courseAccreditationStatus = useAppSelector(
    selectCourseAccreditationsStatusByCourseID(
      domain === 'enduring' ? activeItem || '' : '',
    ),
  )

  let accreditationData =
    domain === 'live' ? eventAccreditationData : courseAccreditationData
  let accreditationStatus =
    domain === 'live' ? eventAccreditationStatus : courseAccreditationStatus

  // Select appropriate list based on domain
  let items = domain === 'live' ? events : courses
  let itemsStatus = domain === 'live' ? eventsStatus : coursesStatus

  // Fetch list data on mount
  useEffect(() => {
    if (domain === 'live' && eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    } else if (domain === 'enduring' && coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [domain, eventsStatus, coursesStatus, dispatch])

  // Fetch v1 migration IDs for enduring domain
  useEffect(() => {
    if (domain === 'enduring' && v1MigrationStatus === 'uninitialized') {
      dispatch(fetchV1MigrationPendingIds())
    }
  }, [domain, v1MigrationStatus, dispatch])

  // Fetch accreditation data when item selected
  useEffect(() => {
    if (!activeItem) return
    if (domain === 'live' && !eventAccreditationData) {
      dispatch(fetchEventAccreditations(activeItem))
    } else if (domain === 'enduring' && !courseAccreditationData) {
      dispatch(fetchCourseAccreditations(activeItem))
    }
  }, [
    activeItem,
    domain,
    eventAccreditationData,
    courseAccreditationData,
    dispatch,
  ])

  // Scroll to details panel on mobile when item selected
  useEffect(() => {
    if (elementRef.current && window.innerWidth < 900) {
      let prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
      ;(elementRef.current as any).scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      })
    }
  }, [activeItem])

  let handleMigrateLive = async () => {
    if (!activeItem || domain !== 'live') return
    let eventName = events.find((e) => e.id === activeItem)?.name
    if (!eventName) return
    setMigrating(true)
    try {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(migrateAccreditationsFromLiveClass({ eventName }))
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchEventAccreditations(activeItem))
    } finally {
      setMigrating(false)
    }
  }

  let handleMigrateCourse = async () => {
    if (!activeItem || domain !== 'enduring') return
    setMigrating(true)
    try {
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(migrateCourseAccreditations(activeItem))
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchCourseAccreditations(activeItem))
      // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
      await dispatch(fetchV1MigrationPendingIds())
    } finally {
      setMigrating(false)
    }
  }

  let hasV1Migration =
    domain === 'enduring' &&
    activeItem &&
    v1MigrationEntries.some((e) => e.courseID === activeItem)

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
        {domain === 'live' ? 'Live Classes (F5)' : 'Enduring Courses (F3)'} -
        Accreditation Dashboard
      </Typography>
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{
          mb: 3,
        }}
      >
        Monitor accreditation status and submissions for{' '}
        {domain === 'live' ? 'live class events' : 'enduring courses'},
        including CAPCE, ANCC, Virginia, CEBroker, New York, and New Jersey
        accreditations across your content portfolio.
      </Typography>
      <AccreditationHealthDashboard domain={domain} onSelectRow={setActiveItem}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <GenerateFloridaApplicationButton
            kind={domain === 'live' ? 'event' : 'course'}
            offeringID={activeItem}
          />
        </Box>

        {/* Main Content Layout */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { sm: '1fr', md: '350px 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {/* Left Column: Event/Course List */}
          <Box
            sx={{
              position: { md: 'sticky' },
              top: { md: 16 },
              maxHeight: { md: 'calc(100vh - 32px)' },
              overflowY: { md: 'auto' },
            }}
          >
            <EventList
              events={items}
              eventsStatus={itemsStatus}
              activeEventId={activeItem}
              onEventSelect={setActiveItem}
              listTitle={domain === 'live' ? 'Events' : 'Courses'}
              showLifecycleFilter={domain === 'live'}
            />
          </Box>

          {/* Right Column: Accreditation Details */}
          <Box ref={elementRef} role="main" aria-label="Accreditation details">
            {!activeItem ? (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'background.paperDark',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Select {domain === 'live' ? 'an Event' : 'a Course'}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Choose {domain === 'live' ? 'an event' : 'a course'} from the
                  list to view accreditation details.
                </Typography>
              </Box>
            ) : accreditationStatus === 'pending' ? (
              <Card variant="outlined" sx={{ p: 3 }}>
                <Skeleton
                  variant="text"
                  sx={{
                    width: '60%',
                    height: 40,
                    mb: 2,
                  }}
                />
                <Skeleton
                  variant="rectangular"
                  sx={{
                    height: 400,
                  }}
                />
              </Card>
            ) : accreditationStatus === 'rejected' ? (
              <Card variant="outlined" sx={{ p: 3, bgcolor: 'error.dark' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Error Loading Accreditation Data
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  Failed to load accreditation information for this{' '}
                  {domain === 'live' ? 'event' : 'course'}. Please try again.
                </Typography>
              </Card>
            ) : accreditationData ? (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="h5" component="h2">
                      Accreditation Overview:{' '}
                      {domain === 'live'
                        ? events.find((e) => e.id === activeItem)?.name
                        : courses.find((c) => c.id === activeItem)?.name}
                    </Typography>
                    {domain === 'live' && (
                      <LifecycleChip
                        lifecycleState={
                          events.find((e) => e.id === activeItem)
                            ?.lifecycleState
                        }
                      />
                    )}
                  </Box>
                  <Box className="flex gap-2">
                    {domain === 'live' && (
                      <Button
                        variant="outlined"
                        startIcon={<MigrateIcon />}
                        onClick={handleMigrateLive}
                        disabled={migrating}
                      >
                        {migrating
                          ? 'Migrating...'
                          : 'Migrate from Old Live Class'}
                      </Button>
                    )}
                    {hasV1Migration && (
                      <Button
                        variant="outlined"
                        startIcon={<MigrateIcon />}
                        onClick={handleMigrateCourse}
                        disabled={migrating}
                      >
                        {migrating
                          ? 'Migrating...'
                          : 'Migrate v1 Accreditations'}
                      </Button>
                    )}
                  </Box>
                </Box>

                <Box className="grid gap-4">
                  <CAPCESection
                    eventId={domain === 'live' ? activeItem : undefined}
                    courseId={domain === 'enduring' ? activeItem : undefined}
                    finalized={accreditationData.capce.finalized}
                    pending={accreditationData.capce.pending}
                  />

                  <ANCCSection
                    eventId={domain === 'live' ? activeItem : undefined}
                    courseId={domain === 'enduring' ? activeItem : undefined}
                    finalized={accreditationData.ancc?.finalized}
                    pending={accreditationData.ancc?.pending}
                  />

                  <VirginiaSection
                    eventId={domain === 'live' ? activeItem : undefined}
                    courseId={domain === 'enduring' ? activeItem : undefined}
                    finalized={accreditationData.virginia.finalized}
                    pending={accreditationData.virginia.pending}
                  />

                  <CEBrokerKentuckySection
                    eventId={domain === 'live' ? activeItem : undefined}
                    courseId={domain === 'enduring' ? activeItem : undefined}
                    finalized={accreditationData.ceBroker.kentucky.finalized}
                    pending={accreditationData.ceBroker.kentucky.pending}
                  />

                  <CEBrokerTennesseeSection
                    eventId={domain === 'live' ? activeItem : undefined}
                    courseId={domain === 'enduring' ? activeItem : undefined}
                    finalized={accreditationData.ceBroker.tennessee.finalized}
                    pending={accreditationData.ceBroker.tennessee.pending}
                  />

                  <CEBrokerFloridaSection
                    eventId={domain === 'live' ? activeItem : undefined}
                    courseId={domain === 'enduring' ? activeItem : undefined}
                    finalized={accreditationData.ceBroker.florida.finalized}
                    pending={accreditationData.ceBroker.florida.pending}
                  />

                  <NewJerseySection
                    eventId={domain === 'live' ? activeItem : undefined}
                    courseId={domain === 'enduring' ? activeItem : undefined}
                    finalized={accreditationData.newJersey.finalized}
                    pending={accreditationData.newJersey.pending}
                  />

                  <NewYorkSection
                    eventId={domain === 'live' ? activeItem : undefined}
                    courseId={domain === 'enduring' ? activeItem : undefined}
                    finalized={accreditationData.newYork.finalized}
                    pending={accreditationData.newYork.pending}
                  />
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'background.paperDark',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No Accreditation Data
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  No accreditation information available for this{' '}
                  {domain === 'live' ? 'event' : 'course'}.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </AccreditationHealthDashboard>
    </Box>
  )
}
